'use client';

// Live "Price Action" tab for the SidePanel — a CLIENT-ONLY, ephemeral view of
// the current symbol's price as it moves. Each fresh /stocks/quotes tick
// (~15s, session-appropriate, last-price only — there is no L2/bid-ask feed) is
// appended to an in-memory series, from which we derive directional stats and a
// live tick tape. A manual refresh on the page (ForceRefreshButton) also pushes
// a tick via the panel context. It holds NOTHING server-side and NOTHING across
// navigation: changing the ticker (or leaving and coming back) clears it.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Activity, Trash2, ArrowUp, ArrowDown, Minus, RefreshCw } from 'lucide-react';
import { useLiveQuotes, POLL_MS } from '@/hooks/useLiveQuotes';
import { useWatchlistPanel } from '@/context/WatchlistPanelContext';
import { deriveTape, medianActiveVolume, relativeVolume, fmtVol, type Tick } from '@/lib/priceActionTape';

const MAX_TICKS = 600;   // series cap (chart / stats)
const TAPE_ROWS = 120;   // rendered tape rows (snappy under the 1s heartbeat)
const UP = '#22c55e';
const DOWN = '#ef4444';
const FLAT = '#94a3b8';
// Direction from a delta ROUNDED to the 2-dp display precision — so a
// floating-point ±1e-13 (same price) reads as flat, not a spurious up/down.
const round2 = (x: number) => Math.round(x * 100) / 100;
const dirOf = (d: number) => { const r = round2(d); return r > 0 ? 1 : r < 0 ? -1 : 0; };
const dirColor = (dir: number) => (dir > 0 ? UP : dir < 0 ? DOWN : FLAT);
const fmtDelta = (x: number) => { const r = round2(x); return r > 0 ? `+${r.toFixed(2)}` : r < 0 ? r.toFixed(2) : '0.00'; };
// RVol tiers for the tape's colour ramp: at/above typical stays neutral, a
// clear burst goes amber, a 2x+ burst goes green.
const RVOL_BURST = 2;
const RVOL_ELEVATED = 1.25;
const AMBER = '#eab308';

function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
const ET_TIME = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
const fmtClock = (t: number) => ET_TIME.format(new Date(t));

export default function PriceActionPanel({ symbol }: { symbol: string }) {
  const sym = symbol ? symbol.toUpperCase() : '';
  const { pricePulse, refreshMs } = useWatchlistPanel();
  // Poll in lockstep with the detail chart's effective "Refresh every" cadence
  // (published to the context), falling back to the hook default elsewhere
  // (e.g. the terminal).
  const pollMs = refreshMs && refreshMs > 0 ? refreshMs : POLL_MS;
  const liveQuotes = useLiveQuotes(sym ? [sym] : [], pollMs);
  const quote = sym ? liveQuotes[sym] : undefined;

  const [ticks, setTicks] = useState<Tick[]>([]);
  const [, setNow] = useState(0); // 1s heartbeat (elapsed clock + refresh countdown)
  const lastPollAtRef = useRef(0); // last AUTO-poll tick time (drives the countdown)
  const lastPulseAtRef = useRef(0);

  useEffect(() => { setTicks([]); lastPollAtRef.current = 0; }, [sym]);
  useEffect(() => {
    const id = setInterval(() => setNow((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Auto-poll appends.
  useEffect(() => {
    if (!quote || !(quote.price > 0)) return;
    lastPollAtRef.current = Date.now();
    const cumVol = typeof quote.volume === 'number' && Number.isFinite(quote.volume) ? quote.volume : null;
    setTicks((prev) => {
      const next = prev.concat({ t: Date.now(), price: quote.price, vol: cumVol });
      return next.length > MAX_TICKS ? next.slice(next.length - MAX_TICKS) : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote]);

  // Manual-refresh append (page ForceRefreshButton → panel context pulse). Only
  // recent, not-yet-seen pulses for THIS symbol — so a stale pulse on mount or a
  // React double-invoke can't double-append.
  useEffect(() => {
    if (!pricePulse || pricePulse.symbol.toUpperCase() !== sym || !(pricePulse.price > 0)) return;
    if (pricePulse.at === lastPulseAtRef.current || Date.now() - pricePulse.at > 8000) return;
    lastPulseAtRef.current = pricePulse.at;
    setTicks((prev) => {
      // PricePulse carries no volume — the next auto-poll's delta will span this
      // gap rather than being lost (see the tape memo).
      const next = prev.concat({ t: pricePulse.at, price: pricePulse.price, vol: null });
      return next.length > MAX_TICKS ? next.slice(next.length - MAX_TICKS) : next;
    });
  }, [pricePulse, sym]);

  const stats = useMemo(() => {
    const n = ticks.length;
    if (n === 0) return null;
    const prices = ticks.map((t) => t.price);
    const open = prices[0];
    const last = prices[n - 1];
    const high = Math.max(...prices);
    const low = Math.min(...prices);
    const avg = prices.reduce((s, p) => s + p, 0) / n;
    let up = 0, down = 0, flat = 0;
    for (let i = 1; i < n; i++) {
      const dir = dirOf(prices[i] - prices[i - 1]);
      if (dir > 0) up++; else if (dir < 0) down++; else flat++;
    }
    // Current run of same-direction ticks (rounded, so noise doesn't reset it).
    let streak = 0, streakDir = 0;
    for (let i = n - 1; i > 0; i--) {
      const dir = dirOf(prices[i] - prices[i - 1]);
      if (dir === 0) break;
      if (streak === 0) { streakDir = dir; streak = 1; }
      else if (dir === streakDir) streak++;
      else break;
    }
    const w = Math.min(6, n);
    const recentNet = round2(last - prices[n - w]);
    const lastT = ticks[n - 1].t;
    let ref = ticks[0];
    for (let i = n - 1; i >= 0; i--) { if (lastT - ticks[i].t >= 60_000) { ref = ticks[i]; break; } }
    return {
      open, last, high, low, avg, up, down, flat,
      streak, streakDir, trend: dirOf(recentNet), velo: round2(last - ref.price),
      chg: round2(last - open), pct: open > 0 ? ((last - open) / open) * 100 : 0,
      first: ticks[0].t, count: n,
    };
  }, [ticks]);

  const tape = useMemo(() => deriveTape(ticks), [ticks]);
  const typicalVol = useMemo(() => medianActiveVolume(tape), [tape]);

  // Seconds until the next auto-poll (approx — based on the last auto tick, at
  // the chart-synced cadence).
  const secsToNext = lastPollAtRef.current
    ? Math.max(0, Math.min(Math.round(pollMs / 1000), Math.ceil((pollMs - (Date.now() - lastPollAtRef.current)) / 1000)))
    : null;

  if (!sym) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-slate-500">
        <Activity size={22} className="opacity-50" />
        <p className="text-xs">Open a stock or ETF to watch its live price action.</p>
      </div>
    );
  }

  const dirLabel = (x: number) => (x > 0 ? 'Rising' : x < 0 ? 'Falling' : 'Flat');
  const DirArrow = ({ x, size = 12 }: { x: number; size?: number }) =>
    x > 0 ? <ArrowUp size={size} /> : x < 0 ? <ArrowDown size={size} /> : <Minus size={size} />;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-default px-3 py-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <Activity size={14} className="flex-shrink-0 text-sky-400" />
          <span className="truncate text-xs font-semibold text-white">{sym}</span>
          <span className="text-[10px] uppercase tracking-wide text-slate-500">live action</span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {secsToNext != null && (
            <span title={`Auto-refresh every ${Math.round(pollMs / 1000)}s — in sync with the chart`} className="flex items-center gap-0.5 font-mono text-[10px] text-slate-500">
              <RefreshCw size={10} className={secsToNext <= 1 ? 'animate-spin' : ''} />{secsToNext}s
            </span>
          )}
          <button
            onClick={() => { setTicks([]); lastPollAtRef.current = 0; }}
            title="Clear and start over"
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-slate-500 hover:bg-card-bg hover:text-slate-300"
          >
            <Trash2 size={11} /> Clear
          </button>
        </div>
      </div>

      {!stats ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-slate-500">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-sky-500" />
          </span>
          <p className="text-xs">Waiting for the next live quote…</p>
          <p className="text-[10px] text-slate-600">Prices fill in automatically every ~{Math.round(pollMs / 1000)}s while this tab is open.</p>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex-shrink-0 space-y-2.5 border-b border-default p-3">
            {/* Last + change */}
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Last</div>
                <div className="font-mono text-2xl font-bold leading-none text-white">{stats.last.toFixed(2)}</div>
              </div>
              <div className="text-right font-mono text-sm font-semibold" style={{ color: dirColor(dirOf(stats.chg)) }}>
                <div>{fmtDelta(stats.chg)}</div>
                <div className="text-xs">{stats.pct >= 0 ? '+' : ''}{stats.pct.toFixed(2)}%</div>
              </div>
            </div>

            {/* Direction / momentum */}
            <div className="grid grid-cols-3 gap-1.5">
              <Stat label="Trend">
                <span className="flex items-center justify-center gap-0.5 font-semibold" style={{ color: dirColor(stats.trend) }}>
                  <DirArrow x={stats.trend} /> {dirLabel(stats.trend)}
                </span>
              </Stat>
              <Stat label="Streak">
                <span className="flex items-center justify-center gap-0.5 font-mono font-semibold" style={{ color: dirColor(stats.streakDir) }}>
                  <DirArrow x={stats.streakDir} size={11} />{stats.streak || 0}
                </span>
              </Stat>
              <Stat label="1-min">
                <span className="font-mono font-semibold" style={{ color: dirColor(dirOf(stats.velo)) }}>{fmtDelta(stats.velo)}</span>
              </Stat>
            </div>

            {/* O / H / L / Avg since you started watching */}
            <div className="grid grid-cols-4 gap-1 rounded-lg border border-default bg-card-bg px-2 py-1.5 text-center">
              {[
                { k: 'Open', v: stats.open }, { k: 'High', v: stats.high },
                { k: 'Low', v: stats.low }, { k: 'Avg', v: stats.avg },
              ].map(({ k, v }) => (
                <div key={k}>
                  <div className="text-[9px] uppercase tracking-wide text-slate-500">{k}</div>
                  <div className="font-mono text-[11px] text-slate-200">{v.toFixed(2)}</div>
                </div>
              ))}
            </div>

            <PriceActionChart ticks={ticks} color={dirColor(dirOf(stats.chg))} open={stats.open} high={stats.high} low={stats.low} last={stats.last} />

            {/* Tick flow */}
            <div>
              <div className="mb-1 flex items-center justify-between text-[10px]">
                <span className="flex items-center gap-0.5 font-mono" style={{ color: UP }}><ArrowUp size={10} />{stats.up}</span>
                <span className="uppercase tracking-wide text-slate-500">Tick flow</span>
                <span className="flex items-center gap-0.5 font-mono" style={{ color: DOWN }}>{stats.down}<ArrowDown size={10} /></span>
              </div>
              <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-card-bg">
                {(() => {
                  const tot = stats.up + stats.down;
                  const upW = tot > 0 ? (stats.up / tot) * 100 : 50;
                  return (<><div style={{ width: `${upW}%`, backgroundColor: UP }} /><div style={{ width: `${100 - upW}%`, backgroundColor: DOWN }} /></>);
                })()}
              </div>
            </div>
          </div>

          {/* Tape */}
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex flex-shrink-0 items-center justify-between border-b border-default px-3 py-1 text-[9px] uppercase tracking-wide text-slate-500">
              <span>Tape</span>
              <span>{stats.count} · {fmtElapsed(Date.now() - stats.first)}</span>
            </div>
            <div className="grid flex-shrink-0 grid-cols-[auto_auto_auto_1fr_auto] items-center gap-2 border-b border-default/60 px-3 py-0.5 text-[9px] uppercase tracking-wide text-slate-600">
              <span>Time</span>
              <span className="w-12 text-right" title="Shares traded since the previous entry — derived from the quote's cumulative session volume, not a trade size.">Vol</span>
              <span className="w-10 text-right" title="This entry's volume against the median of the entries that traded. 2.0x = twice the typical active entry for this session so far.">RVol</span>
              <span className="text-right">&Delta;</span>
              <span className="w-12 text-right">Price</span>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {tape.slice(0, TAPE_ROWS).map((row, i) => {
                const dir = dirOf(row.d);
                const rvol = relativeVolume(row.dv, typicalVol);
                return (
                  <div key={row.t + '-' + i} className="grid grid-cols-[auto_auto_auto_1fr_auto] items-center gap-2 px-3 py-0.5 text-[11px] odd:bg-white/[0.015]">
                    <span className="font-mono text-slate-500">{fmtClock(row.t)}</span>
                    {/* A zero delta and an unknown one both render as a dim dash:
                        nothing traded, or nothing was reported. Matching the flat
                        price cell's language keeps quiet rows visually quiet. */}
                    <span className="w-12 text-right font-mono text-slate-400">
                      {row.dv != null && row.dv > 0 ? fmtVol(row.dv) : <span className="text-slate-600">&mdash;</span>}
                    </span>
                    <span
                      className="w-10 text-right font-mono"
                      style={{ color: rvol == null ? undefined : rvol >= RVOL_BURST ? UP : rvol >= RVOL_ELEVATED ? AMBER : FLAT }}
                    >
                      {rvol != null ? `${rvol.toFixed(1)}x` : <span className="text-slate-600">&mdash;</span>}
                    </span>
                    <span className="flex items-center justify-end gap-0.5 font-mono" style={{ color: dirColor(dir) }}>
                      {dir === 0 ? <Minus size={10} /> : <><DirArrow x={dir} size={10} />{fmtDelta(row.d)}</>}
                    </span>
                    <span className="w-12 text-right font-mono text-slate-200">{row.price.toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-default bg-card-bg px-1.5 py-1 text-center">
      <div className="text-[9px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-[11px]">{children}</div>
    </div>
  );
}

/** Responsive SVG line + area of the tick series with a dotted open baseline,
 *  plus overlaid price labels (High / Low in the corners, Last at the line's
 *  right edge) so the y-axis price context is visible. No chart library. */
function PriceActionChart({ ticks, color, open, high, low, last }: { ticks: Tick[]; color: string; open: number; high: number; low: number; last: number }) {
  const W = 300;
  const H = 110;
  const geom = useMemo(() => {
    const prices = ticks.map((t) => t.price);
    const min = Math.min(...prices, open);
    const max = Math.max(...prices, open);
    const span = max - min || 1;
    const pad = span * 0.12;
    const lo = min - pad;
    const hi = max + pad;
    const yOf = (p: number) => H - ((p - lo) / (hi - lo)) * H;
    const xOf = (i: number) => (ticks.length <= 1 ? W : (i / (ticks.length - 1)) * W);
    const line = ticks.map((t, i) => `${xOf(i).toFixed(1)},${yOf(t.price).toFixed(1)}`).join(' ');
    const area = ticks.length ? `${xOf(0).toFixed(1)},${H} ${line} ${xOf(ticks.length - 1).toFixed(1)},${H}` : '';
    const lastY = yOf(ticks[ticks.length - 1].price);
    return { line, area, baselineY: yOf(open).toFixed(1), lastX: xOf(ticks.length - 1), lastY, lastPct: Math.max(7, Math.min(93, (lastY / H) * 100)) };
  }, [ticks, open]);

  const gid = `pa-fill-${color.replace('#', '')}`;
  return (
    <div className="relative rounded-lg border border-default bg-card-bg p-1">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-28 w-full">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <line x1="0" x2={W} y1={geom.baselineY} y2={geom.baselineY} stroke={FLAT} strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" opacity="0.5" />
        {geom.area && <polygon points={geom.area} fill={`url(#${gid})`} stroke="none" />}
        <polyline points={geom.line} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={geom.lastX} cy={geom.lastY} r="2.5" fill={color} vectorEffect="non-scaling-stroke" />
      </svg>
      {/* Price-axis labels */}
      <span className="pointer-events-none absolute left-1.5 top-1 font-mono text-[9px] text-slate-500">{high.toFixed(2)}</span>
      <span className="pointer-events-none absolute bottom-1 left-1.5 font-mono text-[9px] text-slate-500">{low.toFixed(2)}</span>
      <span
        className="pointer-events-none absolute right-1.5 -translate-y-1/2 rounded px-1 font-mono text-[9px] font-semibold text-white"
        style={{ top: `${geom.lastPct}%`, backgroundColor: color }}
      >
        {last.toFixed(2)}
      </span>
    </div>
  );
}
